import { Component } from '@theme/component';
import { isMobileBreakpoint } from '@theme/utilities';

/**
 * @typedef {Object} TextTruncateRefs
 * @property {HTMLElement} truncateContent - The content wrapper to clamp and animate
 * @property {HTMLButtonElement} truncateButton - The toggle button
 */

/**
 * Clamps text to a configurable number of lines with an animated expand/collapse toggle.
 * Configured through data-lines-desktop, data-lines-mobile, data-truncate-mode and
 * data-show-button.
 *
 * The clamp height is measured from the rendered line boxes rather than multiplying a
 * line-height, so a block mixing font sizes still cuts between lines instead of through
 * one.
 *
 * @extends {Component<TextTruncateRefs>}
 */
class TextTruncateComponent extends Component {
  requiredRefs = ['truncateContent', 'truncateButton'];

  #expanded = false;
  #collapsedHeight = 0;
  #animation;
  #animationSpeed = 300;

  connectedCallback() {
    super.connectedCallback();
    this.#setup();
    window.addEventListener('resize', this.#onResize);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.#onResize);
    this.#animation?.cancel();
  }

  get #linesDesktop() {
    return parseInt(this.dataset.linesDesktop) || 3;
  }

  get #linesMobile() {
    return parseInt(this.dataset.linesMobile) || this.#linesDesktop;
  }

  get #lines() {
    return isMobileBreakpoint() ? this.#linesMobile : this.#linesDesktop;
  }

  get #truncationEnabled() {
    const mode = this.dataset.truncateMode || 'all';
    if (mode === 'mobile') return isMobileBreakpoint();
    if (mode === 'desktop') return !isMobileBreakpoint();
    return true;
  }

  get #showButton() {
    return this.dataset.showButton !== 'false';
  }

  get #inlineButtonLayout() {
    return isMobileBreakpoint()
      ? this.classList.contains('textpro-truncate--mobile-layout-inline')
      : this.classList.contains('textpro-truncate--layout-inline');
  }

  #setup = () => {
    const { truncateContent, truncateButton } = this.refs;
    if (!truncateContent || !truncateButton) return;

    truncateContent.style.removeProperty('max-height');
    truncateContent.style.removeProperty('overflow');
    truncateContent.style.removeProperty('height');
    truncateButton.setAttribute('aria-expanded', 'false');
    this.removeAttribute('data-expanded');

    if (!this.#truncationEnabled) {
      this.#expanded = false;
      truncateButton.hidden = true;
      return;
    }

    truncateButton.hidden = !this.#showButton;
    this.style.setProperty('--textpro-truncate-button-width', `${truncateButton.offsetWidth}px`);

    this.#collapsedHeight = this.#getCollapsedHeight(truncateContent);
    if (this.#showButton && this.#inlineButtonLayout) {
      this.#setInlineButtonPosition(truncateContent, truncateButton);
    }

    // Nothing to hide: the text already fits inside the allowed lines.
    if (truncateContent.scrollHeight <= this.#collapsedHeight + 2) {
      truncateButton.hidden = true;
      return;
    }

    // Clamp regardless; only reveal the toggle when it is enabled.
    truncateButton.hidden = !this.#showButton;
    truncateContent.style.maxHeight = `${this.#collapsedHeight}px`;
    truncateContent.style.overflow = 'hidden';
  };

  /**
   * Walks the text to find where the last visible line ends, so the inline toggle can
   * be placed right after it instead of floating at a fixed offset.
   */
  #setInlineButtonPosition = (truncateContent, truncateButton) => {
    const gap = parseFloat(getComputedStyle(truncateButton).getPropertyValue('--textpro-truncate-gap')) || 8;
    const contentRect = truncateContent.getBoundingClientRect();
    const buttonWidth = Math.ceil(truncateButton.getBoundingClientRect().width);
    const ellipsisWidth = Math.ceil(parseFloat(getComputedStyle(truncateButton).fontSize) * 1.25) || 20;
    const buttonSafetySpace = gap * 2;
    const maxInlineEnd = contentRect.width - buttonWidth - ellipsisWidth - buttonSafetySpace;
    const visibleBlockEnd = contentRect.top + this.#collapsedHeight + 1;
    const textNodes = [];
    const walker = document.createTreeWalker(truncateContent, NodeFilter.SHOW_TEXT);

    while (walker.nextNode()) {
      if (walker.currentNode.textContent.trim()) textNodes.push(walker.currentNode);
    }

    let bestInlineEnd = 0;
    let bestWordInlineEnd = 0;

    for (const node of textNodes) {
      const text = node.textContent;
      const textLength = text.length;

      for (let index = 1; index <= textLength; index++) {
        const range = document.createRange();
        range.setStart(node, index - 1);
        range.setEnd(node, index);

        const rect = range.getBoundingClientRect();
        range.detach();

        if (!rect.width && !rect.height) continue;
        if (rect.bottom > visibleBlockEnd) break;

        const inlineEnd = rect.right - contentRect.left;
        if (inlineEnd <= maxInlineEnd) {
          bestInlineEnd = inlineEnd;
          // Prefer breaking on a word boundary so the ellipsis does not cut a word.
          if (/\s/.test(text.charAt(index)) || index === textLength) {
            bestWordInlineEnd = inlineEnd;
          }
        }
      }
    }

    const inlineEnd = bestWordInlineEnd || bestInlineEnd;
    const buttonInlineStart = Math.min(inlineEnd + ellipsisWidth + gap, contentRect.width - buttonWidth);
    this.style.setProperty('--textpro-truncate-button-width', `${buttonWidth}px`);
    this.style.setProperty(
      '--textpro-truncate-button-height',
      `${Math.ceil(truncateButton.getBoundingClientRect().height)}px`
    );
    this.style.setProperty('--textpro-truncate-ellipsis', `${ellipsisWidth}px`);
    this.style.setProperty('--textpro-truncate-button-start', `${Math.max(0, buttonInlineStart)}px`);
  };

  #onResize = () => {
    // Re-measuring while expanded would collapse the block under the reader.
    if (this.#expanded && this.#truncationEnabled) return;
    this.#setup();
  };

  toggle = (event) => {
    event.preventDefault();
    if (!this.#truncationEnabled || !this.#showButton) return;

    const { truncateContent, truncateButton } = this.refs;
    const expanding = !this.#expanded;

    const startHeight = truncateContent.offsetHeight;
    let endHeight;

    if (expanding) {
      truncateContent.style.removeProperty('max-height');
      endHeight = truncateContent.scrollHeight;
    } else {
      this.#collapsedHeight = this.#getCollapsedHeight(truncateContent);
      endHeight = this.#collapsedHeight;
    }

    this.dataset.expanded = expanding.toString();
    truncateButton.setAttribute('aria-expanded', expanding.toString());

    this.#animateHeight(truncateContent, startHeight, endHeight, expanding);
  };

  #animateHeight = (el, startHeight, endHeight, expanding) => {
    el.style.overflow = 'hidden';
    this.#animation?.cancel();

    this.#animation = el.animate(
      { height: [`${startHeight}px`, `${endHeight}px`] },
      { duration: this.#animationSpeed, easing: 'ease-in-out' }
    );

    this.#animation.onfinish = () => {
      el.style.removeProperty('height');
      if (expanding) {
        el.style.removeProperty('max-height');
        el.style.removeProperty('overflow');
      } else {
        el.style.maxHeight = `${this.#collapsedHeight}px`;
        el.style.overflow = 'hidden';
      }
      this.#expanded = expanding;
    };
  };

  /**
   * Measures the bottom of the Nth rendered line by grouping the text rects into lines,
   * so mixed font sizes still clamp on a line boundary. Falls back to line-height when
   * the content has fewer lines than requested.
   */
  #getCollapsedHeight = (truncateContent) => {
    const contentRect = truncateContent.getBoundingClientRect();
    const lines = [];
    const walker = document.createTreeWalker(truncateContent, NodeFilter.SHOW_TEXT);

    while (walker.nextNode()) {
      const node = walker.currentNode;
      if (!node.textContent.trim()) continue;

      const range = document.createRange();
      range.selectNodeContents(node);

      for (const rect of range.getClientRects()) {
        if (!rect.width && !rect.height) continue;

        const top = rect.top - contentRect.top;
        const bottom = rect.bottom - contentRect.top;
        let line = lines.find((item) => Math.abs(item.top - top) < 3);

        if (!line) {
          line = { top, bottom };
          lines.push(line);
        }

        line.top = Math.min(line.top, top);
        line.bottom = Math.max(line.bottom, bottom);
      }

      range.detach();
    }

    lines.sort((a, b) => a.top - b.top);

    if (lines.length >= this.#lines) {
      return Math.ceil(lines[this.#lines - 1].bottom);
    }

    const lineHeight = parseFloat(getComputedStyle(truncateContent).lineHeight) || 24;
    return Math.round(lineHeight * this.#lines);
  };
}

if (!customElements.get('text-truncate-component')) {
  customElements.define('text-truncate-component', TextTruncateComponent);
}
